import { computed, ref } from 'vue'
import { fetchContentSnippets, snippetsToMap } from '@/api/content'
import { formatSameDayCutoffText } from '@/utils/mallShipping'

const cache = ref<Record<string, string>>({})
const loading = ref(false)

export const SAME_DAY_CUTOFF_KEY = 'mall.product.same_day_cutoff'

export const SNIPPET_FALLBACKS: Record<string, string> = {
  'mall.product.trust_badge': '장기요양기관 납품 기준으로 검수한 상품입니다.',
  'mall.product.shipping_notes':
    '<ul><li>도서·산간 지역은 배송 일정이 달라질 수 있습니다.</li><li>토스페이먼츠 결제가 완료되면 판매업체로 주문이 전달됩니다.</li></ul>',
  'mall.product.return_policy':
    '<ul><li>당일 출고 마감·교환·반품 기준은 판매업체가 안내한 정책을 따릅니다.</li><li>상품 하자·오배송 시 수령 후 판매업체 안내에 따라 교환·반품이 가능합니다.</li><li>단순 변심 반품은 판매업체가 안내한 반품 배송비를 뺀 금액이 환불됩니다.</li></ul>',
  [SAME_DAY_CUTOFF_KEY]: '평일 {cutoff} 이전 결제 완료 시',
  'mall.checkout.payment_guide':
    '결제는 토스페이먼츠로 합니다. 승인이 끝나면 판매업체로 주문이 전달됩니다.',
  'mall.cart.shipping_note':
    '배송비는 상품마다 다릅니다. 주문당 상품은 그중 최댓값, 개·박스마다 받는 상품은 수량으로 계산해 더합니다.',
  /* 비우면 홈 이벤트 배너 숨김. 관리자 화면 멘트에서 HTML로 채운다. */
  'mall.home.promo_html': '',
  'portal.home.service_notice':
    '장기요양지원센터는 임금·회계·고시·함우리몰을 한곳에서 지원합니다.',
  'seller.onboard.intro':
    '관리자가 등록한 계정입니다. 사업자 정보·정산 계좌를 입력하고 약관에 서명한 뒤 검수를 요청해 주세요. 승인 후 판매가 시작됩니다.',
}

export function useContentSnippets() {
  async function loadSnippets(keys: string[]) {
    const missing = keys.filter((key) => cache.value[key] === undefined)
    if (!missing.length) {
      return cache.value
    }
    loading.value = true
    try {
      const rows = await fetchContentSnippets(missing)
      cache.value = { ...cache.value, ...snippetsToMap(rows) }
    } catch {
      /* 폴백 사용 */
    } finally {
      loading.value = false
    }
    return cache.value
  }

  function snippet(key: string): string {
    /* 관리자가 비운 멘트는 폴백으로 되살리지 않는다. */
    if (Object.prototype.hasOwnProperty.call(cache.value, key)) {
      return cache.value[key]
    }
    return SNIPPET_FALLBACKS[key] || ''
  }

  return {
    cache,
    loading,
    loadSnippets,
    snippet,
  }
}

/** 상품상세·스토어의 당일 출고 한 줄. 멘트가 비면 숨긴다. */
export function useSameDayCutoffText(getCutoff: () => string | undefined | null) {
  const { loadSnippets, snippet } = useContentSnippets()
  const sameDayCutoffText = computed(() =>
    formatSameDayCutoffText(snippet(SAME_DAY_CUTOFF_KEY), getCutoff()),
  )
  function loadSameDayCutoffSnippet() {
    return loadSnippets([SAME_DAY_CUTOFF_KEY])
  }
  return { sameDayCutoffText, loadSameDayCutoffSnippet }
}
